/**
 * MilestoneManager Component
 * Manages CRUD operations for item milestones
 */

import { useState } from 'react';
import { Plus, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { useItemMilestones, useDeleteMilestone } from '@/hooks/milestones/useItemMilestones';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';
import { useItemMilestonePermissions } from '@/hooks/milestones/useItemMilestonePermissions';
import { toast } from 'sonner';
import { useMethodLayerCounts } from '@/hooks/method/useMethodLayerCounts';
import { useUpdateMilestoneSortOrder } from '@/hooks/milestones/useMilestoneReorder';
import { useProjectTeamMembers } from '@/hooks/useProjectTeamMembers';
import { MilestoneForm } from './MilestoneForm';
import { SortableMilestoneCard } from './SortableMilestoneCard';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { logger } from '@/lib/logger';
import type { ItemMilestone } from '@/schemas/milestone';

interface MilestoneManagerProps {
  itemId: string;
  projectId: string;
  projectStatus?: string; // ✅ Pass project status for service item assignment
  itemStartDate: string;
  itemEndDate: string;
}

export const MilestoneManager = ({ itemId, projectId, projectStatus, itemStartDate, itemEndDate }: MilestoneManagerProps) => {
  // ✅ HARDCODED (2025-11-09): Always enabled
  const milestoneFeatureEnabled = true;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<ItemMilestone | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [isReorderMode, setIsReorderMode] = useState(false);

  const { data: milestones = [], isLoading } = useItemMilestones(itemId);
  const { data: milestoneTypes = [] } = useMilestoneTypes();
  const deleteMutation = useDeleteMilestone();
  const updateSortOrder = useUpdateMilestoneSortOrder();
  const { canView, canCreate, canEdit, canDelete } = useItemMilestonePermissions(projectId);

  /** Mốc đang có mặt bằng ở tab Method → chặn xoá: FK method_layers.milestone_id
      là ON DELETE CASCADE + item_milestones hard delete ⇒ xoá mốc là mất sạch
      mặt bằng và mọi hình đã vẽ, không khôi phục được. */
  const methodLayerCountsQ = useMethodLayerCounts(projectId, true);

  // Fetch team members for assignee display (service projects)
  const isServiceProject = projectStatus === 'service';
  const { data: teamMembers = [] } = useProjectTeamMembers(projectId, { 
    enabled: isServiceProject 
  });

  if (!milestoneFeatureEnabled || !canView) {
    return null;
  }

  const handleAdd = () => {
    setEditingMilestone(null);
    setIsFormOpen(true);
    logger.debug('MilestoneManager', 'Opening add milestone form', { itemId });
  };

  const handleEdit = (milestone: ItemMilestone) => {
    setEditingMilestone(milestone);
    setIsFormOpen(true);
    logger.debug('MilestoneManager', 'Opening edit milestone form', { milestoneId: milestone.id });
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    await deleteMutation.mutateAsync({ 
      id: deleteConfirm.id, 
      itemId 
    });
    setDeleteConfirm(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = milestones.findIndex(m => m.id === active.id);
    const newIndex = milestones.findIndex(m => m.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Reorder array
    const newMilestones = arrayMove(milestones, oldIndex, newIndex);
    
    // Update sort_order for all milestones
    const updates = newMilestones.map((m, idx) => ({
      id: m.id,
      sort_order: idx
    }));
    
    updateSortOrder.mutate({ itemId, updates });
  };

  const getTypeColor = (typeName: string) => {
    const type = milestoneTypes.find(t => t.name === typeName);
    return type?.color || '#6b7280';
  };

  const getTypeDisplayName = (typeName: string) => {
    const type = milestoneTypes.find(t => t.name === typeName);
    return type?.display_name || typeName;
  };

  const getStatusVariant = (status: ItemMilestone['status']): 'default' | 'secondary' | 'outline' | 'destructive' => {
    switch (status) {
      case 'completed': return 'default';
      case 'in_progress': return 'secondary';
      case 'blocked': return 'destructive';
      default: return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">Loading milestones...</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {milestones.length} milestone{milestones.length !== 1 ? 's' : ''}
        </p>
        <div className="flex gap-2">
          {milestones.length > 1 && canEdit && (
            <Button 
              onClick={() => setIsReorderMode(!isReorderMode)} 
              size="sm"
              variant={isReorderMode ? "default" : "outline"}
            >
              <GripVertical className="h-4 w-4 mr-2" />
              {isReorderMode ? 'Xong' : 'Sắp xếp'}
            </Button>
          )}
          {canCreate && (
            <Button onClick={handleAdd} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Milestone
            </Button>
          )}
        </div>
      </div>

      {milestones.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">No milestones yet</p>
            {canCreate && (
              <Button onClick={handleAdd} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add First Milestone
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={milestones.map(m => m.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {milestones.map((milestone, index) => (
                <SortableMilestoneCard
                  key={milestone.id}
                  milestone={milestone}
                  index={index}
                  isReorderMode={isReorderMode}
                  onEdit={handleEdit}
                  onDelete={(id, name) => {
                    const n = methodLayerCountsQ.data?.get(id) ?? 0;
                    if (n > 0) {
                      toast.error(`Mốc này đang có ${n} mặt bằng ở tab Method`, {
                        description: 'Xoá hết mặt bằng của mốc bên Method trước, rồi mới xoá mốc.',
                      });
                      return;
                    }
                    setDeleteConfirm({ id, name });
                  }}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  getTypeColor={getTypeColor}
                  getTypeDisplayName={getTypeDisplayName}
                  getStatusVariant={getStatusVariant}
                  teamMembers={teamMembers}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingMilestone ? 'Edit Milestone' : 'Add Milestone'}
            </DialogTitle>
          </DialogHeader>
          <MilestoneForm
            itemId={itemId}
            projectId={projectId}
            projectStatus={projectStatus}
            itemStartDate={itemStartDate}
            itemEndDate={itemEndDate}
            milestone={editingMilestone}
            onSuccess={() => setIsFormOpen(false)}
            onCancel={() => setIsFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title="Delete Milestone"
        description={`Are you sure you want to delete "${deleteConfirm?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
